import React, { useState, useEffect } from 'react'
import { Gem, Loader, XCircle, Package, Edit3 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import sbpApi from '../services/sbpApi'
import sbp2Api from '../services/sbp2Api'
import { closePreparedPaymentWindow, prepareExternalPaymentWindow } from '../utils/openExternalPaymentLink'

/**
 * SbpPayment Component
 * Универсальный компонент для оплаты через СБП
 * Поддерживает два режима: готовые пакеты и произвольная сумма
 */
const SbpPayment = ({ method = 'sbp' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isSbp2 = method === 'sbp2'
  const api = isSbp2 ? sbp2Api : sbpApi
  const packagePriceKey = isSbp2 ? 'sbp2' : 'sbp1'

  // Состояния для табов
  const [activeTab, setActiveTab] = useState('packages') // 'packages' или 'custom'

  // Состояния для конфигурации СБП
  const [sbpConfig, setSbpConfig] = useState(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [configError, setConfigError] = useState(null)

  // Состояния для пакетов
  const [packages, setPackages] = useState([])
  const [isLoadingPackages, setIsLoadingPackages] = useState(true)
  const [packagesError, setPackagesError] = useState(null)

  // Состояния для произвольной суммы
  const [rublesInput, setRublesInput] = useState('')
  const [crystalsInput, setCrystalsInput] = useState('')
  const [customAmountError, setCustomAmountError] = useState('')

  // Состояния для обработки платежа
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingPackageId, setProcessingPackageId] = useState(null)

  // Загрузка конфигурации СБП при монтировании
  useEffect(() => {
    loadSbpConfig()
  }, [])

  // Загрузка пакетов при монтировании
  useEffect(() => {
    loadPackages()
  }, [])

  /**
   * Загрузить конфигурацию СБП (курсы и лимиты)
   */
  const loadSbpConfig = async () => {
    setIsLoadingConfig(true)
    setConfigError(null)

    try {
      const config = await api.fetchSbpConfig()
      setSbpConfig(config)
      console.log('✅ SBP config loaded:', config)
    } catch (error) {
      console.error('❌ Error loading SBP config:', error)
      setConfigError(t('payment.sbp.configError'))
    } finally {
      setIsLoadingConfig(false)
    }
  }

  /**
   * Загрузить доступные пакеты для СБП
   */
  const loadPackages = async () => {
    setIsLoadingPackages(true)
    setPackagesError(null)

    try {
      const sbpPackages = await api.fetchSbpPackages()
      
      // Сортируем пакеты: самый дешевый первый, затем популярные
      const sortedPackages = [...sbpPackages].sort((a, b) => {
        const minPrice = Math.min(...sbpPackages.map(p => p[packagePriceKey]))
        
        if (a[packagePriceKey] === minPrice && b[packagePriceKey] !== minPrice) return -1
        if (b[packagePriceKey] === minPrice && a[packagePriceKey] !== minPrice) return 1
        
        if (a.popular && !b.popular) return -1
        if (b.popular && !a.popular) return 1
        
        return a[packagePriceKey] - b[packagePriceKey]
      })
      
      setPackages(sortedPackages)
      console.log('✅ SBP packages loaded:', sortedPackages)
    } catch (error) {
      console.error('❌ Error loading SBP packages:', error)
      setPackagesError(t('payment.sbp.packagesError'))
    } finally {
      setIsLoadingPackages(false)
    }
  }

  /**
   * Форматировать число с пробелами (разделитель тысяч)
   */
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  }

  /**
   * Обработка оплаты готового пакета
   */
  const handlePackagePayment = async (pkg) => {
    if (isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()

    setIsProcessing(true)
    setProcessingPackageId(pkg.id)

    try {
      console.log(`💰 Создание платежа СБП для пакета:`, pkg)
      
      // Создаем платеж по количеству кристаллов
      const { link } = await api.createPaymentByCrystals(pkg.crystals)
      
      // Открываем ссылку на оплату
      api.openSbpPayment(link, () => {
        // После открытия ссылки показываем toast и перенаправляем на главную
        toast.success('Вы получите уведомление после успешной оплаты', {
          duration: 4000,
          icon: '💳'
        })
        
        setTimeout(() => {
          navigate('/')
        }, 1500)
      }, paymentWindow)
      
    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа:', error)
      
      // Обработка специфичных ошибок
      if (error.message === 'INVALID_AMOUNT') {
        toast.error(t('payment.sbp.errorInvalidAmount', { min: sbpConfig?.min_rub || 100, max: sbpConfig?.max_rub || 50000 }))
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(t('payment.sbp.errorBackend'))
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
      setProcessingPackageId(null)
    }
  }

  /**
   * Отображение готовых пакетов
   */
  const renderPackages = () => {
    // Состояние загрузки
    if (isLoadingPackages) {
      return (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="bg-surface rounded-lg border border-white border-opacity-10 p-4 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-600 rounded-lg"></div>
                  <div>
                    <div className="h-5 bg-gray-600 rounded w-20 mb-2"></div>
                    <div className="h-4 bg-gray-600 rounded w-16"></div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="h-5 bg-gray-600 rounded w-12 mb-2"></div>
                  <div className="h-6 bg-gray-600 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }

    // Состояние ошибки
    if (packagesError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{packagesError}</p>
          <button
            onClick={loadPackages}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('payment.sbp.retry')}
          </button>
        </div>
      )
    }

    // Список пакетов
    return (
      <div className="grid grid-cols-1 gap-4">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className={`relative bg-surface rounded-lg border border-white border-opacity-10 p-4 cursor-pointer transition-all hover:border-accent-primary/50 hover:scale-[1.02] active:scale-[0.98] ${
              pkg.popular ? 'ring-2 ring-accent-primary ring-opacity-50' : ''
            } ${
              isProcessing && processingPackageId === pkg.id 
                ? 'opacity-50 cursor-not-allowed' 
                : ''
            }`}
            onClick={() => !isProcessing && handlePackagePayment(pkg)}
          >
            {/* Бейдж "Популярно" */}
            {pkg.popular && (
              <div className="absolute -top-2 -right-2">
                <span className="inline-block px-3 py-1 bg-accent-primary text-dark-primary text-xs font-bold rounded-full shadow-lg">
                  {t('payment.popular')}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              {/* Левая часть - иконка и количество кристаллов */}
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                  <Gem size={28} className="text-dark-primary" />
                </div>
                <div>
                  <p className="text-white font-bold text-xl">
                    {formatNumber(pkg.crystals)}
                  </p>
                  <p className="text-text-secondary text-sm">
                    {t('payment.crystals')}
                  </p>
                </div>
              </div>
              
              {/* Правая часть - цена в рублях или индикатор загрузки */}
              <div className="text-right">
                {isProcessing && processingPackageId === pkg.id ? (
                  <div className="flex items-center gap-2 text-accent-primary">
                    <Loader size={20} className="animate-spin" />
                  </div>
                ) : (
                  <div className="text-accent-primary font-bold text-lg">
                    <span>{formatNumber(pkg[packagePriceKey])} ₽</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  /**
   * Обработка изменения поля "Рубли"
   */
  const handleRublesChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '') // Только цифры
    setRublesInput(value)
    setCustomAmountError('')

    if (value && sbpConfig) {
      // Конвертируем рубли в кристаллы и округляем
      const crystals = Math.round(parseFloat(value) * sbpConfig.price_one_rub_in_crystal)
      setCrystalsInput(crystals.toString())
    } else {
      setCrystalsInput('')
    }
  }

  /**
   * Обработка изменения поля "Кристаллы"
   */
  const handleCrystalsChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '') // Только цифры
    setCrystalsInput(value)
    setCustomAmountError('')

    if (value && sbpConfig) {
      // Конвертируем кристаллы в рубли и округляем
      const rubles = Math.round(parseFloat(value) * sbpConfig.price_one_crystal_in_rub)
      setRublesInput(rubles.toString())
    } else {
      setRublesInput('')
    }
  }

  /**
   * Валидация суммы
   */
  const validateCustomAmount = () => {
    if (!rublesInput || !sbpConfig) {
      setCustomAmountError(t('payment.sbp.enterAmount'))
      return false
    }

    const rubles = parseFloat(rublesInput)

    if (rubles < sbpConfig.min_rub || rubles > sbpConfig.max_rub) {
      setCustomAmountError(t('payment.sbp.errorInvalidAmount', { min: formatNumber(sbpConfig.min_rub), max: formatNumber(sbpConfig.max_rub) }))
      return false
    }

    return true
  }

  /**
   * Обработка оплаты произвольной суммы
   */
  const handleCustomPayment = async () => {
    if (!validateCustomAmount() || isProcessing) return

    const paymentWindow = prepareExternalPaymentWindow()

    setIsProcessing(true)

    try {
      console.log(`💰 Создание платежа СБП на сумму: ${rublesInput}₽`)

      // Создаем платеж по сумме в рублях
      const { link } = await api.createPaymentByRubles(parseFloat(rublesInput))

      // Открываем ссылку на оплату
      api.openSbpPayment(link, () => {
        // После открытия ссылки показываем toast и перенаправляем на главную
        toast.success(t('payment.sbp.paymentNotification'), {
          duration: 4000,
          icon: '💳'
        })

        setTimeout(() => {
          navigate('/')
        }, 1500)
      }, paymentWindow)

    } catch (error) {
      closePreparedPaymentWindow(paymentWindow)
      console.error('❌ Ошибка создания платежа:', error)

      // Обработка специфичных ошибок
      if (error.message === 'INVALID_AMOUNT') {
        setCustomAmountError(t('payment.sbp.errorInvalidAmount', { min: formatNumber(sbpConfig.min_rub), max: formatNumber(sbpConfig.max_rub) }))
      } else if (error.message === 'BACKEND_ERROR') {
        toast.error(t('payment.sbp.errorBackend'))
      } else {
        toast.error(t('payment.createError'))
      }
    } finally {
      setIsProcessing(false)
    }
  }

  /**
   * Отображение формы произвольной суммы
   */
  const renderCustomAmount = () => {
    // Состояние загрузки конфигурации
    if (isLoadingConfig) {
      return (
        <div className="text-center py-8">
          <Loader size={48} className="text-accent-primary mx-auto mb-4 animate-spin" />
          <p className="text-text-secondary">{t('payment.sbp.loadingConfig')}</p>
        </div>
      )
    }

    // Состояние ошибки конфигурации
    if (configError) {
      return (
        <div className="text-center py-8">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{configError}</p>
          <button
            onClick={loadSbpConfig}
            className="px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('payment.sbp.retry')}
          </button>
        </div>
      )
    }

    const hasError = customAmountError !== ''
    const isValidAmount = rublesInput && !hasError && parseFloat(rublesInput) >= sbpConfig.min_rub && parseFloat(rublesInput) <= sbpConfig.max_rub

    return (
      <div className="space-y-6">
        {/* Информация о лимитах */}
        <div className="text-center">
          <p className="text-text-secondary text-sm">
            {t('payment.sbp.minMax', { min: formatNumber(sbpConfig.min_rub), max: formatNumber(sbpConfig.max_rub) })}
          </p>
        </div>

        {/* Поле ввода рублей */}
        <div>
          <label className="block text-sm font-semibold text-white mb-2">
            {t('payment.sbp.rubles')}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={rublesInput}
            onChange={handleRublesChange}
            placeholder={t('payment.sbp.enterAmount')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        {/* Поле ввода кристаллов */}
        <div>
          <label className="block text-sm font-semibold text-white mb-2">
            {t('payment.sbp.crystalsAmount')}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={crystalsInput}
            onChange={handleCrystalsChange}
            placeholder={t('payment.sbp.enterCrystals')}
            className={`w-full px-4 py-3 bg-surface rounded-lg border ${
              hasError ? 'border-red-500' : 'border-white border-opacity-10'
            } text-white text-lg font-semibold focus:outline-none focus:border-accent-primary transition-colors`}
          />
        </div>

        {/* Сообщение об ошибке */}
        {hasError && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm text-center">{customAmountError}</p>
          </div>
        )}

        {/* Кнопка оплаты */}
        <button
          onClick={handleCustomPayment}
          disabled={!isValidAmount || isProcessing}
          className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
            isValidAmount && !isProcessing
              ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <Loader size={20} className="animate-spin" />
              {t('payment.sbp.paying')}
            </span>
          ) : (
            `${t('payment.sbp.pay')} ${rublesInput ? formatNumber(rublesInput) : '0'} ₽`
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto">
      {/* Табы переключения режимов */}
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveTab('packages')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${activeTab === 'packages'
                ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Package size={18} className="inline mr-2" />
            {t('payment.sbp.tabPackages')}
          </button>
          
          <button
            onClick={() => setActiveTab('custom')}
            className={`
              px-4 py-3 rounded-lg font-semibold transition-all
              ${activeTab === 'custom'
                ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary'
                : 'bg-surface text-text-secondary hover:text-white border border-white border-opacity-10'
              }
            `}
          >
            <Edit3 size={18} className="inline mr-2" />
            {t('payment.sbp.tabCustom')}
          </button>
        </div>
      </div>

      {/* Контент в зависимости от выбранного таба */}
      {activeTab === 'packages' ? renderPackages() : renderCustomAmount()}
    </div>
  )
}

export default SbpPayment
